import { useTranslation } from "@privacy/translations";
import { Box, Stack, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import type { GridViewRegistration } from "../../../api/registrations.types";
import { DynamicGrid } from "../../../dynamicGrid";
import type { SidePanelGroup } from "../../../dynamicGrid/SidePanels";
import { usePrivacyNavigate } from "../../../shared/usePrivacyNavigate";
import { RegistrationControllerIcon2, RegistrationProcessorIcon2 } from "../../../shared/icons";
import { Drawer } from "../../../shared/Drawer";
import { useRegistrationVariant } from "../RegistrationVariantProvider";

export const SidePanelGrid = ({
  gridHeader,
  sidePanelGroup,
  registrations,
  onShowInTableClick,
}: {
  gridHeader: string;
  sidePanelGroup: SidePanelGroup;
  registrations: GridViewRegistration[];
  onShowInTableClick?: () => void;
}) => {
  const { t } = useTranslation();
  const { navigate, paths } = usePrivacyNavigate();
  const goToSidePanel = DynamicGrid.useGoToSidePanel();
  const registrationVariant = useRegistrationVariant();

  const Icon2 = registrationVariant === "Controller" ? RegistrationControllerIcon2 : RegistrationProcessorIcon2;

  return (
    <Stack sx={{ height: "100%" }}>
      <Stack direction="row" sx={{ px: 2, pb: 3, alignItems: "center", gap: 2 }}>
        <DynamicGrid.SubSidePanelBackButton panelGroup={sidePanelGroup} />
        <Typography variant="h3" sx={{ fontWeight: "600" }}>
          {gridHeader}

          <Box component="span" sx={{ ml: 1, fontWeight: 400 }}>
            {`(${registrations.length})`}
          </Box>
        </Typography>
        {onShowInTableClick && (
          <Button
            variant="text"
            size="small"
            sx={{ color: theme => theme.palette.primary["600"], ml: "auto", flexShrink: 0 }}
            onClick={() => {
              onShowInTableClick();
              goToSidePanel(null);
            }}
          >
            {t("RegistrationGrid.SidePanels.ShowInTable")}
          </Button>
        )}
      </Stack>
      <Drawer.Grid
        getRowId={row => row.registrationId}
        onRowClick={row => navigate(paths.registrationsInline({ id: String(row.id), variant: registrationVariant }))}
        rows={registrations}
        slots={{
          toolbar: Drawer.GridToolbar,
          noRowsOverlay: NoRowsOverlay,
        }}
        partsSx={{ mainSx: { borderBottom: "none" } }}
        columns={[
          {
            field: "name",
            flex: 1,
            renderHeader: () => "",
            valueGetter: (_, { name }) => name,
            renderCell: ({ row }) => (
              <Drawer.GridCell>
                <Stack direction="row" sx={{ gap: 2, alignItems: "center" }}>
                  <Icon2 size="sm" />
                  <Stack>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: "600", lineHeight: 1.2, overflowWrap: "anywhere", hyphens: "auto" }}
                    >
                      {row.name}
                    </Typography>
                  </Stack>
                </Stack>
              </Drawer.GridCell>
            ),
          },
        ]}
      />
    </Stack>
  );
};

const NoRowsOverlay = () => {
  return <Drawer.GridNoRowsOverlay labelKey="RegistrationGrid.NoRows" />;
};
